import { MutableRefObject, useEffect } from "react";
import { CollectColumn, Details } from "../types";
import { ColumnNode, SeeMoreNode, SelfConnectingEdge, TableNode } from "./CustomNodes";
import ReactFlow, {
  Background,
  Controls,
  Edge,
  Node,
  ReactFlowInstance,
  ReactFlowProvider,
} from "reactflow";
import { createNewNodesEdges, layoutElementsOnCanvas, staticProcessColumnLineage } from "../graph";
import { useLineageContext, useLineageDispatch } from "../context/LineageProvider";
import { setCollectColumns, setSidebarScreen } from "../context/LineageSlice";
import { LineageModal } from "./LineageModals";
import { findSourcesSinks, getCurrentTableId, handleTableNavigation } from "../utils";
import { SidebarModal } from "./sidebarModal/SidebarModal";
import { MoreTables } from "./MoreTables";
import { TableDetails } from "./TableDetails";
import ExposureDetails from "./ExposureDetails";
import FunctionDetails from "./FunctionDetails";
import SidebarErrorBoundary from "./SidebarErrorBoundary";
import { COLUMNS_SIDEBAR, EXPOSURE_SIDEBAR, FUNCTION_SIDEBAR, TABLES_SIDEBAR } from "../constants";
import LineageLegend from "./LineageLegend";

const nodeTypes = {
  table: TableNode,
  seeMore: SeeMoreNode,
  column: ColumnNode,
};
const edgeTypes = {
  selfConnecting: SelfConnectingEdge,
};

type StaticLineageProps = {
  flow: MutableRefObject<ReactFlowInstance<unknown, unknown> | undefined>;
  selectedColumn: { table: string; name: string };
  collectColumns: Record<string, CollectColumn[]>;
  columnEdges?: [string, string, string][];
  tableEdges: [string, string][];
  details: Details;
};

const StaticLineage = ({
  flow,
  selectedColumn,
  collectColumns,
  columnEdges = [],
  tableEdges,
  details,
}: StaticLineageProps) => {
  const {
    state: { sidebarScreen },
  } = useLineageContext();
  const lineageDispatch = useLineageDispatch();

  useEffect(() => {
    lineageDispatch(setCollectColumns(collectColumns));
    setTimeout(async () => {
      const _createTable = (table: string) => ({
        table,
        label: details[table].name!,
        upstreamCount: 0,
        downstreamCount: 0,
        nodeType: details[table].type || "cte",
        isExternalProject: false,
        tests: [],
      });
      const { sources } = findSourcesSinks(tableEdges);
      let nodes: Node[] = [];
      let edges: Edge[] = [];
      const queue = [...sources];
      const visited: Record<string, boolean> = {};
      const getConnectedTables = (right: boolean, curr: string) => {
        const connectedTables = right
          ? tableEdges.filter(([src]) => src === curr).map(([, dst]) => dst)
          : tableEdges.filter(([, dst]) => dst === curr).map(([src]) => src);
        const currLevel = nodes.find(n => n.id === curr)?.data?.level || 0;
        createNewNodesEdges(
          nodes,
          edges,
          connectedTables.map(_createTable),
          curr,
          right,
          currLevel,
          10000,
          false,
          details
        );
        return connectedTables;
      };
      while (queue.length > 0) {
        const curr = queue.pop()!;
        if (visited[curr]) continue;
        visited[curr] = true;
        queue.push(...getConnectedTables(true, curr), ...getConnectedTables(false, curr));
      }

      if (selectedColumn) {
        const columnRK = `${selectedColumn.table}/${selectedColumn.name}`;
        const { nodes: _nodes, edges: _edges } = await staticProcessColumnLineage(
          nodes,
          edges,
          columnRK,
          async () => ({
            collect_columns: collectColumns,
            highlight_edges: columnEdges,
          })
        );
        nodes = _nodes;
        edges = _edges;
      }

      layoutElementsOnCanvas(nodes, edges, false);
      flow.current?.setNodes(nodes);
      flow.current?.setEdges(edges);
    }, 500);
  }, [collectColumns, columnEdges, details, flow, lineageDispatch, selectedColumn, tableEdges]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      flow.current?.fitView({ duration: 500 });
    }, 1000);
    return () => clearInterval(timerId);
  }, [flow]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div style={{ width: "100%", height: "100%" }}>
        <ReactFlowProvider>
          <ReactFlow
            defaultNodes={[]}
            defaultEdges={[]}
            onInit={_flow => (flow.current = _flow)}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            style={{ background: "var(--bg-color)" }}
            proOptions={{ hideAttribution: true }}
            minZoom={0.05}
          >
            <Background />
            <Controls />
          </ReactFlow>
          <LineageLegend />
          <SidebarModal
            isOpen={sidebarScreen !== ""}
            closeModal={() => lineageDispatch(setSidebarScreen(""))}
            width="30vw"
          >
            <SidebarErrorBoundary key={sidebarScreen}>
              {sidebarScreen === TABLES_SIDEBAR && <MoreTables />}
              {sidebarScreen === COLUMNS_SIDEBAR && (
                <TableDetails
                  lineageType="static"
                  currentTableId={getCurrentTableId()}
                  onNavigate={handleTableNavigation}
                />
              )}
              {sidebarScreen === EXPOSURE_SIDEBAR && <ExposureDetails />}
              {sidebarScreen === FUNCTION_SIDEBAR && <FunctionDetails />}
            </SidebarErrorBoundary>
          </SidebarModal>
        </ReactFlowProvider>
      </div>
      <LineageModal />
    </div>
  );
};

export default StaticLineage;
